<script setup lang="ts">
  import { computed, inject, onBeforeUnmount, onMounted, reactive, ref, watch, watchEffect } from 'vue'
  import { pendingRefocusKey } from '@/shared/models/pendingRefocus'
  import {
    Form,
    Input,
    InputNumber,
    Select,
    SelectOption,
    Checkbox,
    Button,
    AutoComplete
  } from 'ant-design-vue'
  import { CalculationSchema } from '../models/CalculationSchema'
  import { CalculationField } from '../models/CalculationField'
  import { CalculationFieldType } from '../models/CalculationFieldType'
  import { CalculationsService } from '../services/CalculationsService'
  import { debounceTime, Subject } from 'rxjs'
  import { parseSmartValue, DEFAULT_INPUT_DEBOUNCE_MS } from '@/utils'
  import { MinusSquare, PlusSquare } from 'lucide-vue-next'
  import JsonCalculationConfiguration from './JsonCalculationConfiguration.vue'
  import SelectOrCreateCalculationConfiguration from './SelectOrCreateCalculationConfiguration.vue'
  import ColorCalculationConfiguration from './ColorCalculationConfiguration.vue'
  import ColumnMultiSelectOrCreateCalculationConfiguration from './ColumnMultiSelectOrCreateCalculationConfiguration.vue'
  import { CompressOutlined, ExpandOutlined } from '@ant-design/icons-vue'
  import { CalculationsJsonCalculationConfigurationService } from '../services/CalculationJsonCalculationConfigurationService'
  import ArrayCalculationBuilder from './ArrayCalculationBuilder.vue'
  import RegexCalculationConfiguration from './RegexCalculationConfiguration.vue'
  import CalculationHelpPopover from '../help/components/CalculationHelpPopover.vue'
  import StringTemplateBuilder from '@/shared/StringTemplateBuilder/components/StringTemplateBuilder.vue'
  import { useWindowSize } from '@/composables/useWindowSize'
  import { CalculationsUiService } from '../services/CalculationsUiService'
  import { Calculation } from '../models/Calculation'

  interface IObjectCalculationBuilderProps {
    calculationSchemas: Array<CalculationSchema>
    calculationField: CalculationField
    modelValue: {}
    availableColumns: Array<{ value: string; label: string }>
    availableParams: Array<{ value: string; label: string }>
    configurationLevel: number
    isCondensedLayout: boolean
    globalCollapsed?: { collapsed: boolean }
    rootCalculation?: Calculation | null
    currentPath?: string[]
  }

  interface IObjectCalculationBuilderEmits {
    (e: 'update:modelValue', value: {}): void
  }

  const props = defineProps<IObjectCalculationBuilderProps>()
  const emit = defineEmits<IObjectCalculationBuilderEmits>()

  const modelValue = reactive<{}>(props.modelValue)
  const configuration = ref<Record<string, any>>({})
  const isCollapsed = ref<boolean>(false)
  const globalCollapsed = ref<{ collapsed: boolean }>(props.globalCollapsed ?? { collapsed: false })

  // Initialize configuration with default values
  const initializeConfiguration = () => {
    if (props.calculationField.field_type !== CalculationFieldType.Object) return
    if (
      (props.calculationField.item_schema == null && props.calculationField.fields == null) ||
      (props.calculationField.item_schema != null &&
        props.calculationField.item_schema.field_type !== CalculationFieldType.Object)
    )
      throw new Error(
        'ObjectCalculationBuilder requires an Object type with fields or child items_schema of type'
      )

    if (props.calculationField.fields != null) {
      configuration.value = CalculationsService.initializeCalculationFieldConfiguration(
        props.calculationField.fields!
      )
    } else if (props.calculationField.item_schema != null) {
      configuration.value = CalculationsService.initializeCalculationConfigurationForField(
        props.calculationField.item_schema
      )
    }
  }

  const handleConfigurationChange = () => {
    if (props.calculationField) {
      emit('update:modelValue', configuration.value)
    }
  }

  // @ts-expect-error
  const handleCalculationTypeChanged = (calculationType: string, value: {}) => {
    //TODO: Support object nested custom calculations and type conversion
    //if (props.calculation) {
    //emit('configuration-updated', { ...props.calculation, type: calculationType }, value, true)
    //}
  }

  const pendingRefocus = inject(pendingRefocusKey)

  let isUnmounting = false

  const onInputChange = (newValue: string, fieldName: string) => {
    const activeEl = document.activeElement as HTMLInputElement | null
    if (pendingRefocus) {
      pendingRefocus.value = {
        levelFieldKey: `${props.configurationLevel}:${fieldName}`,
        cursor: activeEl?.selectionStart ?? null
      }
    }
    inputValueChanged$.next({ value: newValue, field: fieldName })
  }

  const onInputBlur = (fieldKey: string) => {
    if (isUnmounting) return
    if (pendingRefocus?.value?.levelFieldKey === `${props.configurationLevel}:${fieldKey}`) {
      pendingRefocus.value = null
    }
  }

  const inputValueChanged$ = new Subject<{ field: string; value: string }>()

  const inputValueChangedSubscription = inputValueChanged$
    .pipe(debounceTime(DEFAULT_INPUT_DEBOUNCE_MS))
    .subscribe(({ field, value }) => {
      const parsedValue = parseSmartValue(value)
      configuration.value[field] = parsedValue
      handleConfigurationChange()
    })

  watch(
    modelValue,
    (newVal) => {
      configuration.value = { ...newVal }
    },
    { immediate: true }
  )

  watch(
    () => props.calculationField,
    (newCalculationField) => {
      if (newCalculationField) {
        if (Object.keys(configuration.value).length === 0) {
          initializeConfiguration()
          handleConfigurationChange()
        }
      }
    },
    { immediate: true }
  )

  watchEffect(() => {
    const newVal = props.globalCollapsed
    if (newVal) {
      globalCollapsed.value = newVal
      isCollapsed.value = !!newVal.collapsed
    }
  })

  const toggleCollapse = () => {
    isCollapsed.value = !isCollapsed.value
  }

  const toggleGlobalCollapse = () => {
    globalCollapsed.value = { collapsed: !isCollapsed.value }
    isCollapsed.value = !isCollapsed.value
  }

  const simpleFieldCount = computed(() =>
    props?.calculationField?.fields
      ? props.calculationField.fields.filter(
          (f: any) =>
            f.field_type !== CalculationFieldType.Object &&
            f.field_type !== CalculationFieldType.Array &&
            f.field_type !== CalculationFieldType.Json
        ).length
      : 0
  )

  const shouldShowGlobalCollapseButton = computed(() => {
    return (
      props.calculationField?.fields?.findIndex(
        (x: CalculationField) =>
          x.field_type === CalculationFieldType.Array ||
          x.field_type === CalculationFieldType.Object ||
          x.field_type === CalculationFieldType.Json
      ) !== -1
    )
  })

  const shouldShowFieldInformation = (field: CalculationField): boolean => {
    return (
      field.field_type !== CalculationFieldType.Object &&
      field.field_type !== CalculationFieldType.Json
    )
  }

  const isObjectWithNestedObject = computed(
    () => props.calculationField.fields == null && props.calculationField.item_schema != null
  )

  const { controlSize } = useWindowSize()
  const checkboxSizeClass = computed(() => {
    if (controlSize.value === 'small') return 'checkbox-sm'
    if (controlSize.value === 'large') return 'checkbox-lg'
    return 'checkbox-md'
  })

  const displayField = computed(() => {
    const mappedField = props.calculationField?.display_field as
      | string
      | string[]
      | null
      | undefined
    if (mappedField == null) return undefined

    const fields = props.calculationField?.fields ?? []

    const valueForName = (name: string) => {
      const matchingField = fields.find((x) => x.name === name)
      if (!matchingField) return undefined
      const val = configuration.value?.[matchingField.name]
      return val === undefined ? undefined : val
    }

    if (typeof mappedField === 'string') {
      return valueForName(mappedField)
    }

    if (Array.isArray(mappedField)) {
      for (const name of mappedField) {
        const v = valueForName(name)
        if (v) return v
      }
    }

    return undefined
  })

  onMounted(() => {
    if (pendingRefocus?.value) {
      const { levelFieldKey, cursor } = pendingRefocus.value
      let el = document.querySelector(`[data-field="${levelFieldKey}"]`) as HTMLElement | null
      if (el && el.tagName !== 'INPUT') el = el.querySelector('input') ?? el
      const inputEl = el as HTMLInputElement | null
      if (inputEl?.tagName === 'INPUT') {
        inputEl.focus()
        if (cursor !== null) {
          try { inputEl.setSelectionRange(cursor, cursor) } catch {}
        }
      }
      pendingRefocus.value = null
    }
  })

  onBeforeUnmount(() => {
    isUnmounting = true
    inputValueChangedSubscription?.unsubscribe()
  })
</script>

<template>
  <div v-if="isObjectWithNestedObject" class="object-calculation-builder-grid-span">
    <ObjectCalculationBuilder
      :configuration-level="props.configurationLevel"
      :available-columns="props.availableColumns"
      :available-params="props.availableParams"
      v-model="configuration[props.calculationField.item_schema!.name]"
      :calculation-field="props.calculationField.item_schema!"
      @update:modelValue="handleConfigurationChange"
      :calculation-schemas="calculationSchemas"
      :is-condensed-layout="props.isCondensedLayout"
      :global-collapsed="globalCollapsed"
      :root-calculation="props.rootCalculation"
    />
  </div>

  <div v-else>
    <div
      v-if="calculationField"
      :class="[
        'border',
        'calculation-configuration',
        'h-full',
        'w-full',
        {
          'child-calculation-configuration': props.configurationLevel > 1,
          '!bg-[var(--card)] dark:!bg-[var(--card-light)]': props.configurationLevel % 2 !== 0,
          '!bg-[var(--card-light)] dark:!bg-[var(--card)]': props.configurationLevel % 2 === 0
        }
      ]"
    >
      <div class="flex h-full flex-col">
        <div class="horizontal-rule mb-4 border-b pb-1">
          <div class="flex">
            <div>
              <h3 class="mb-1 text-lg font-semibold">
                Configure: {{ calculationField.display_name }}
                <span v-if="displayField != null">{{ `(${displayField})` }}</span>
              </h3>
            </div>

            <div class="ml-auto flex" v-if="props.configurationLevel > 1">
              <a-tooltip placement="top">
                <template #title>Toggle Collapse</template>
                <Button
                  style="padding: 3px; border: none"
                  type="text"
                  :size="controlSize"
                  class="flex items-center justify-center"
                  @click="toggleCollapse"
                  aria-label="Toggle Collapse"
                >
                  <PlusSquare
                    v-if="isCollapsed"
                    class="text-base"
                    style="line-height: 1; margin: 0"
                  />
                  <MinusSquare v-else class="text-base" style="line-height: 1; margin: 0" />
                </Button>
              </a-tooltip>

              <a-tooltip v-if="shouldShowGlobalCollapseButton" placement="top">
                <template #title>{{ isCollapsed ? 'Expand All' : 'Collapse All' }}</template>
                <Button
                  style="padding: 3px; border: none"
                  type="text"
                  size="large"
                  class="flex items-center justify-center"
                  @click="toggleGlobalCollapse"
                  aria-label="Toggle Collapse All"
                >
                  <CompressOutlined
                    v-if="isCollapsed"
                    class="rotate-90 rounded-sm border-gray-300 p-1 text-base"
                    style="line-height: 1; margin: 0"
                  />
                  <ExpandOutlined
                    v-else
                    class="rotate-90 rounded-sm border-gray-300 p-1 text-base"
                    style="line-height: 1; margin: 0"
                  />
                </Button>
              </a-tooltip>
            </div>
          </div>
        </div>

        <div
          :class="[
            'flex-1',
            'overflow-y-auto',
            { 'collapsed-calculation-configuration': isCollapsed }
          ]"
        >
          <Form ref="formRef" :model="configuration" layout="vertical">
            <div
              :class="{
                'object-calculation-fields-grid': true,
                expanded: !props.isCondensedLayout
              }"
            >
              <div
                v-for="field in calculationField.fields"
                :key="field.name"
                class="object-calculation-form-field-grid"
                :class="{
                  'array-calculation-builder-grid-span':
                    field.field_type === CalculationFieldType.Array,
                  'object-calculation-builder-grid-span':
                    field.field_type === CalculationFieldType.Object,
                  'json-calculation-configuration-grid-span':
                    field.field_type === CalculationFieldType.Json,
                  'single-field-grid-span':
                    simpleFieldCount <= 1 &&
                    field.field_type !== CalculationFieldType.Object &&
                    field.field_type !== CalculationFieldType.Array &&
                    field.field_type !== CalculationFieldType.Json,
                  '!hidden': !CalculationsUiService.isFieldVisibleFromRoot(
                    field,
                    props.rootCalculation,
                    props.currentPath ? [...props.currentPath, field.name] : [field.name]
                  )
                }"
              >
                <div class="flex">
                  <div v-if="shouldShowFieldInformation(field)" class="min-w-0 flex-1">
                    <label class="calculation-field-label mb-1 block text-sm font-medium">
                      {{ field.display_name }}
                      <span v-if="field.required" class="text-red-500">*</span>
                    </label>
                    <p v-if="field.description" class="calculation-field-label mb-2 text-xs">
                      {{ field.description }}
                    </p>
                  </div>
                  <div
                    v-if="field.help_content != null && field.help_content?.length > 0"
                    class="m1-auto flex justify-end"
                  >
                    <CalculationHelpPopover
                      :content-type="field.help_content"
                      :title="`${field.display_name} Help`"
                    />
                  </div>
                </div>

                <!-- Text Input -->
                <Input
                  v-if="field.field_type === CalculationFieldType.Text"
                  :value="configuration[field.name]"
                  @update:value="(val: string) => onInputChange(val, field.name)"
                  @blur="() => onInputBlur(field.name)"
                  :data-field="`${props.configurationLevel}:${field.name}`"
                  :placeholder="field.placeholder || ''"
                  :readonly="field.readonly"
                  :disabled="field.readonly"
                  :size="controlSize"
                />

                <!-- Number Input -->
                <InputNumber
                  v-else-if="field.field_type === CalculationFieldType.Number"
                  v-model:value="configuration[field.name]"
                  :placeholder="field.placeholder || ''"
                  :size="controlSize"
                  @change="handleConfigurationChange"
                  :disabled="field.readonly"
                  :readonly="field.readonly"
                  class="w-full"
                />

                <!-- Regex Input -->
                <RegexCalculationConfiguration
                  v-else-if="field.field_type === CalculationFieldType.RegularExpression"
                  v-model="configuration[field.name]"
                  :required="field.required"
                  :regex="field.field_type_args?.regex_pattern"
                  :data-type="field.field_type_args?.regex_data_type"
                  :placeholder="field.placeholder || ''"
                  :error-message="field.field_type_args?.regex_description"
                  @update:modelValue="handleConfigurationChange"
                />

                <!-- Select Dropdown -->
                <Select
                  v-else-if="field.field_type === CalculationFieldType.Select"
                  v-model:value="configuration[field.name]"
                  :placeholder="`Select ${field.display_name.toLowerCase()}`"
                  @change="handleConfigurationChange"
                  :size="controlSize"
                  :disabled="field.readonly"
                  :readonly="field.readonly"
                  class="w-full"
                  showSearch
                  allowClear
                >
                  <SelectOption
                    v-for="option in field.options"
                    :key="option.value"
                    :value="option.value"
                  >
                    {{ option.label }}
                  </SelectOption>
                </Select>

                <!-- Select Dropdown JSON Payload -->
                <Select
                  v-else-if="field.field_type === CalculationFieldType.SelectJson"
                  :value="JSON.stringify(configuration[field.name])"
                  :placeholder="`Select ${field.display_name.toLowerCase()}`"
                  @update:value="
                    (val: string) => {
                      try {
                        const parsed = JSON.parse(val)
                        configuration[field.name] = parsed
                        handleConfigurationChange()
                      } catch (e) {
                        console.error('Failed to parse selected JSON value', e)
                      }
                    }
                  "
                  :size="controlSize"
                  :disabled="field.readonly"
                  :readonly="field.readonly"
                  class="w-full"
                  showSearch
                  allowClear
                >
                  <SelectOption
                    v-for="option in field.options"
                    :key="JSON.stringify(option.value)"
                    :value="JSON.stringify(option.value)"
                  >
                    <span v-html="option.label"></span>
                  </SelectOption>
                </Select>

                <!-- Select Dropdown Or Create -->
                <SelectOrCreateCalculationConfiguration
                  v-else-if="field.field_type === CalculationFieldType.SelectOrCreate"
                  v-model="configuration[field.name]"
                  :placeholder="`Select or enter new ${field.display_name.toLowerCase()}`"
                  :options="field.options || []"
                  :disabled="field.readonly"
                  :readonly="field.readonly"
                  @update:modelValue="handleConfigurationChange"
                />

                <!-- Multi-Select -->
                <Select
                  v-else-if="
                    field.field_type === CalculationFieldType.MultiSelect ||
                    field.field_type === CalculationFieldType.ColumnMultiSelect ||
                    field.field_type === CalculationFieldType.ParamMultiSelect
                  "
                  v-model:value="configuration[field.name]"
                  mode="multiple"
                  :placeholder="`Select ${field.display_name.toLowerCase()}`"
                  @change="handleConfigurationChange"
                  :size="controlSize"
                  :disabled="field.readonly"
                  :readonly="field.readonly"
                  class="w-full"
                  showSearch
                  allowClear
                >
                  <SelectOption
                    v-if="field.field_type === CalculationFieldType.MultiSelect"
                    v-for="option in field.options"
                    :key="option.value"
                    :value="option.value"
                  >
                    {{ option.label }}
                  </SelectOption>

                  <SelectOption
                    v-if="field.field_type === CalculationFieldType.ColumnMultiSelect"
                    v-for="option in props.availableColumns"
                    :key="option.value"
                    :value="option.value"
                  >
                    {{ option.label }}
                  </SelectOption>

                  <SelectOption
                    v-if="field.field_type === CalculationFieldType.ParamMultiSelect"
                    v-for="option in props.availableParams"
                    :key="option.value"
                    :value="option.value"
                  >
                    {{ option.label }}
                  </SelectOption>
                </Select>

                <!-- Boolean Checkbox -->
                <Checkbox
                  v-else-if="field.field_type === CalculationFieldType.Boolean"
                  @change="handleConfigurationChange"
                  v-model:checked="configuration[field.name]"
                  :disabled="field.readonly"
                  :readonly="field.readonly"
                  :class="checkboxSizeClass"
                >
                  {{ field.display_name }}
                </Checkbox>

                <!-- Column Select -->
                <Select
                  v-else-if="field.field_type === CalculationFieldType.ColumnSelect"
                  v-model:value="configuration[field.name]"
                  :placeholder="`Select ${field.display_name.toLowerCase()}`"
                  @change="handleConfigurationChange"
                  :size="controlSize"
                  :disabled="field.readonly"
                  :readonly="field.readonly"
                  class="w-full"
                  showSearch
                  allowClear
                >
                  <SelectOption
                    v-for="column in props.availableColumns"
                    :key="column.value"
                    :value="column.value"
                  >
                    {{ column.label }}
                  </SelectOption>
                </Select>

                <!-- Param Select -->
                <Select
                  v-else-if="field.field_type === CalculationFieldType.ParamSelect"
                  v-model:value="configuration[field.name]"
                  :placeholder="`Select ${field.display_name.toLowerCase()}`"
                  @change="handleConfigurationChange"
                  :size="controlSize"
                  class="w-full"
                  :disabled="field.readonly"
                  :readonly="field.readonly"
                  showSearch
                  allowClear
                >
                  <SelectOption
                    v-for="column in props.availableParams"
                    :key="column.value"
                    :value="column.value"
                  >
                    {{ column.label }}
                  </SelectOption>
                </Select>

                <!-- Column Select or Create -->
                <AutoComplete
                  v-else-if="field.field_type === CalculationFieldType.ColumnSelectOrCreate"
                  v-model:value="configuration[field.name]"
                  :options="props.availableColumns"
                  :placeholder="
                    field.placeholder || `Select existing column or enter new column name`
                  "
                  :size="controlSize"
                  class="w-full"
                  :disabled="field.readonly"
                  :readonly="field.readonly"
                  @change="handleConfigurationChange"
                  allow-clear
                  showSearch
                />

                <!-- Param Select or Create -->
                <AutoComplete
                  v-else-if="field.field_type === CalculationFieldType.ParamSelectOrCreate"
                  v-model:value="configuration[field.name]"
                  :options="props.availableParams"
                  :placeholder="
                    field.placeholder || `Select existing param or enter new param name`
                  "
                  :size="controlSize"
                  class="w-full"
                  :disabled="field.readonly"
                  :readonly="field.readonly"
                  @change="handleConfigurationChange"
                  showSearch
                  allowClear
                />

                <!-- Multi Column Select Or Create -->
                <ColumnMultiSelectOrCreateCalculationConfiguration
                  v-else-if="field.field_type === CalculationFieldType.ColumnMultiSelectOrCreate"
                  v-model="configuration[field.name]"
                  :placeholder="`Select or enter new ${field.display_name.toLowerCase()} values`"
                  :available-columns="props.availableColumns"
                  @update:modelValue="handleConfigurationChange"
                />

                <!-- Multi Param Select Or Create -->
                <ColumnMultiSelectOrCreateCalculationConfiguration
                  v-else-if="field.field_type === CalculationFieldType.ParamMultiSelectOrCreate"
                  v-model="configuration[field.name]"
                  :placeholder="`Select or enter new ${field.display_name.toLowerCase()} values`"
                  :available-columns="props.availableParams"
                  @update:modelValue="handleConfigurationChange"
                />

                <!-- Color Picker -->
                <ColorCalculationConfiguration
                  v-else-if="field.field_type === CalculationFieldType.ColorPicker"
                  v-model="configuration[field.name]"
                  :place-holder="field.placeholder || 'Select color'"
                  @update:modelValue="handleConfigurationChange"
                />

                <!-- Array Type -->
                <ArrayCalculationBuilder
                  v-else-if="field.field_type === CalculationFieldType.Array"
                  :configuration-level="props.configurationLevel"
                  :available-columns="props.availableColumns"
                  :available-params="props.availableParams"
                  v-model="configuration[field.name]"
                  :calculation-field="field"
                  @update:modelValue="handleConfigurationChange"
                  :calculation-schemas="calculationSchemas"
                  :is-condensed-layout="props.isCondensedLayout"
                  :global-collapsed="globalCollapsed"
                />

                <!-- Object Type -->
                <ObjectCalculationBuilder
                  v-else-if="field.field_type === CalculationFieldType.Object"
                  :configuration-level="props.configurationLevel + 1"
                  :available-columns="props.availableColumns"
                  :available-params="props.availableParams"
                  v-model="configuration[field.name]"
                  :calculation-field="field"
                  @update:modelValue="handleConfigurationChange"
                  :calculation-schemas="calculationSchemas"
                  :is-condensed-layout="props.isCondensedLayout"
                  :global-collapsed="globalCollapsed"
                  :root-calculation="props.rootCalculation"
                  :current-path="
                    props.currentPath ? [...props.currentPath, field.name] : [field.name]
                  "
                />

                <!-- String Template -->
                <StringTemplateBuilder
                  v-else-if="field.field_type === CalculationFieldType.StringTemplate"
                  :configuration-level="props.configurationLevel + 1"
                  :available-columns="props.availableColumns"
                  :delimiter_start="field.field_type_args?.delimiter_start"
                  :delimiter_end="field.field_type_args?.delimiter_end"
                  v-model="configuration[field.name]"
                  :place-holder="field.placeholder"
                  @update:modelValue="handleConfigurationChange"
                  :is-condensed-layout="props.isCondensedLayout"
                  :global-collapsed="globalCollapsed"
                />

                <!-- Json Type (Custom Config) -->
                <JsonCalculationConfiguration
                  v-else-if="field.field_type === CalculationFieldType.Json"
                  :calculation-schemas="props.calculationSchemas"
                  :available-columns="props.availableColumns"
                  v-model="configuration"
                  :editor-schema="
                    CalculationsJsonCalculationConfigurationService.getJsonEditorSchemaForCalculation()
                  "
                  :place-holder="field.placeholder || 'Configure Json'"
                  :configuration-level="props.configurationLevel"
                  @calculation-type-changed="handleCalculationTypeChanged"
                  @update:modelValue="handleConfigurationChange"
                  :allow-type-change="true"
                />

                <!-- Default fallback for unknown field types -->
                <JsonCalculationConfiguration
                  v-else
                  :calculation-schemas="props.calculationSchemas"
                  :available-columns="props.availableColumns"
                  v-model="configuration[field.name]"
                  :editor-schema="
                    CalculationsJsonCalculationConfigurationService.getJsonEditorSchemaForField(
                      field
                    )
                  "
                  :place-holder="field.placeholder || 'Configure Json'"
                  :configuration-level="props.configurationLevel"
                  @update:modelValue="handleConfigurationChange"
                  :allow-type-change="false"
                />
              </div>
            </div>
          </Form>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
  .collapsed-calculation-configuration {
    display: none;
  }

  .calculation-configuration {
    padding: 10px;
    border-radius: 4px;
  }

  .horizontal-rule {
    border-bottom-color: #0000001f;
  }

  .object-calculation-fields-grid {
    display: grid;
    grid-template-columns: 1fr;
    gap: 16px;
  }

  @media (min-width: 1100px) {
    .object-calculation-fields-grid:not(.expanded) {
      grid-template-columns: repeat(2, 1fr);
    }
  }

  .object-calculation-form-field-grid {
    width: 100%;
    min-height: 90px;
    max-height: 110px;
    display: flex;
    flex-direction: column;
    justify-content: flex-start;
    overflow: hidden;
  }

  .array-calculation-builder-grid-span,
  .object-calculation-builder-grid-span,
  .json-calculation-configuration-grid-span {
    grid-column: 1 / -1 !important;
    min-height: unset;
    max-height: unset;
    overflow: visible;
  }
</style>
